import { useEffect, useState } from 'react';
import { makeResetCache, useCacheQuery } from 'gossamer-front-end';
import { LivestreamBreakoutRoom, LivestreamBreakoutRoomParticipant } from 'cb-model';
import { useParticipants } from '@livekit/components-react';
import { useLivestreamSessionContext, useVideoConferenceContext } from '../../hooks';
import { Button } from 'react-bootstrap';
import {
  assignBreakoutParticipant,
  broadcastToBreakoutRooms,
  closeBreakoutRooms,
  createBreakoutRooms,
  getBreakoutPresence,
  LivestreamApiResponse,
} from '../../api';
import { notify } from 'gossamer-front-end';
import { DoorClosed, Megaphone, Minus, Plus } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { isHostParticipantMetadata } from '../../viewerLivestreamState';

const mainRoomOption = { id: 'main', shortLabel: 'M', fullLabel: 'Main room' };

const getRoomShortLabel = (roomName: string, fallbackIndex: number) => {
  const match = roomName.match(/(\d+)$/);
  return match?.[1] ?? String(fallbackIndex + 1);
};

const RoomAssignmentControl = ({
  identity,
  assignedRoomId,
  currentRoomId,
  openRooms,
  onAssign,
}: {
  identity: string;
  assignedRoomId: string;
  currentRoomId?: string;
  openRooms: Array<{ id: string; name: string }>;
  onAssign: (identity: string, breakoutRoomId: string | null) => void;
}) => {
  const options = [
    mainRoomOption,
    ...openRooms.map((room, index) => ({
      id: room.id,
      shortLabel: getRoomShortLabel(room.name, index),
      fullLabel: room.name,
    })),
  ];

  return (
    <div className="flex items-center gap-1.5 shrink-0">
      {options.map((option) => {
        const checked = assignedRoomId === option.id;
        const isCurrentRoom = currentRoomId === option.id;

        return (
          <label
            key={option.id}
            className={[
              'relative flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border text-[10px] font-semibold transition-colors',
              checked
                ? 'border-blue-500 bg-blue-600 text-white'
                : 'border-[#4a4a4a] bg-[#2b2b2b] text-gray-300 hover:border-[#6a6a6a] hover:text-white',
              isCurrentRoom ? 'ring-2 ring-emerald-400 ring-offset-1 ring-offset-[#1e1e1e]' : '',
            ].join(' ')}
            title={`${option.fullLabel}${isCurrentRoom ? ' - currently in this room' : ''}`}
          >
            <input
              type="radio"
              className="sr-only"
              name={`breakout-assignment-${identity}`}
              value={option.id}
              checked={checked}
              onChange={() => {
                if (option.id !== assignedRoomId) {
                  onAssign(identity, option.id === 'main' ? null : option.id);
                }
              }}
            />
            <span>{option.shortLabel}</span>
          </label>
        );
      })}
    </div>
  );
};

export const BreakoutRoomsPanel = () => {
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);
  const { extendedWidgetState } = useVideoConferenceContext();
  const { roomId } = useLivestreamSessionContext();
  const participants = useParticipants();
  const session = useSessionContext();

  const [roomCount, setRoomCount] = useState(2);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [currentRoomByIdentity, setCurrentRoomByIdentity] = useState<Record<string, string>>({});
  const [displayNameByIdentity, setDisplayNameByIdentity] = useState<Record<string, string>>({});
  const [authFailed, setAuthFailed] = useState(false);

  const hasSession = session.loading === false && session.doesSessionExist;
  const canUseBreakoutApi = Boolean(roomId) && hasSession && !authFailed;

  const getResponseError = (response: Partial<LivestreamApiResponse> | undefined) => {
    if (!response) {
      return 'Request failed';
    }
    return response.error || response.message || (response.status && response.status >= 400 ? 'Request failed' : '');
  };

  const markAuthFailure = (response?: Partial<LivestreamApiResponse>) => {
    if (response?.status === 401 || response?.message === 'try refresh token') {
      setAuthFailed(true);
      return true;
    }
    return false;
  };

  const breakoutRoomsQuery = useCacheQuery(
    LivestreamBreakoutRoom,
    {
      where: { and: [{ fieldId: 'livestream_session_id', operator: 'eq', value: roomId }] },
      limit: 20,
    },
    !canUseBreakoutApi,
  );

  const openRooms = breakoutRoomsQuery.data?.filter((r) => r.status === 'OPEN') ?? [];
  const hasOpenRooms = openRooms.length > 0;
  const openRoomIds = openRooms.map((room) => room.id);

  const breakoutAssignmentsQuery = useCacheQuery(
    LivestreamBreakoutRoomParticipant,
    {
      where: {
        and:
          openRoomIds.length > 0
            ? [{ fieldId: 'breakout_room_id', operator: 'in', value: openRoomIds }]
            : [{ fieldId: 'breakout_room_id', operator: 'eq', value: '__none__' }],
      },
      limit: 200,
    },
    !canUseBreakoutApi || openRoomIds.length === 0,
  );

  const liveKitParticipants = participants.filter((participant) => !isHostParticipantMetadata(participant.metadata));
  const requestedRoomByIdentity = (breakoutAssignmentsQuery.data ?? []).reduce<Record<string, string>>((acc, row) => {
    acc[row.identity] = row.breakout_room_id;
    return acc;
  }, {});
  const participantRows = Array.from(
    new Set([
      ...liveKitParticipants.map((participant) => participant.identity),
      ...Object.keys(requestedRoomByIdentity),
    ]),
  )
    .map((identity) => {
      const liveKitParticipant = liveKitParticipants.find((participant) => participant.identity === identity);
      const currentRoomId = currentRoomByIdentity[identity] ?? 'main';
      const requestedRoomId = requestedRoomByIdentity[identity] ?? currentRoomId;

      return {
        identity,
        displayName: liveKitParticipant?.name || displayNameByIdentity[identity] || identity,
        currentRoomId,
        requestedRoomId,
      };
    })
    .sort((left, right) => left.displayName.localeCompare(right.displayName));

  useEffect(() => {
    if (!canUseBreakoutApi || !hasOpenRooms || !extendedWidgetState.showBreakoutRooms) {
      setCurrentRoomByIdentity({});
      setDisplayNameByIdentity({});
      return;
    }

    let cancelled = false;

    const loadPresence = async () => {
      try {
        const response = await getBreakoutPresence({ livestreamSessionId: roomId });
        if (markAuthFailure(response)) {
          return;
        }
        if (response.error) {
          throw new Error(response.error);
        }
        if (!cancelled) {
          setCurrentRoomByIdentity(response.result?.memberships ?? {});
          setDisplayNameByIdentity(response.result?.displayNames ?? {});
        }
      } catch {
        if (!cancelled) {
          setCurrentRoomByIdentity({});
          setDisplayNameByIdentity({});
        }
      }
    };

    void loadPresence();
    const intervalId = window.setInterval(loadPresence, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [canUseBreakoutApi, extendedWidgetState.showBreakoutRooms, hasOpenRooms, roomId]);

  useEffect(() => {
    if (hasSession) {
      setAuthFailed(false);
    }
  }, [hasSession]);

  const handleCreate = async () => {
    if (!roomId || !hasSession) return;
    setIsCreating(true);
    try {
      const rooms = Array.from({ length: roomCount }, (_, i) => ({ name: `Room ${i + 1}` }));
      const response = await createBreakoutRooms({ livestreamSessionId: roomId, rooms });
      if (markAuthFailure(response) || response.error) {
        notify([getResponseError(response) || 'Failed to create breakout rooms', 'danger']);
        return;
      }
      resetCache('LivestreamBreakoutRoom');
      notify([`${roomCount} breakout room(s) opened`, 'success']);
    } catch {
      notify(['Failed to create breakout rooms', 'danger']);
    } finally {
      setIsCreating(false);
    }
  };

  const handleAssign = async (identity: string, breakoutRoomId: string | null) => {
    if (!roomId || !hasSession) return;
    try {
      const response = await assignBreakoutParticipant({ livestreamSessionId: roomId, breakoutRoomId, identity });
      if (markAuthFailure(response) || response.error) {
        notify([getResponseError(response) || 'Failed to assign participant', 'danger']);
        return;
      }
      resetCache('LivestreamBreakoutRoomParticipant');
    } catch {
      notify(['Failed to assign participant', 'danger']);
    }
  };

  const handleClose = async () => {
    if (!roomId || !hasSession) return;
    setIsClosing(true);
    try {
      const response = await closeBreakoutRooms({ livestreamSessionId: roomId });
      if (markAuthFailure(response) || response.error) {
        notify([getResponseError(response) || 'Failed to close breakout rooms', 'danger']);
        return;
      }
      resetCache('LivestreamBreakoutRoom');
      notify(['All breakout rooms closed', 'success']);
    } catch {
      notify(['Failed to close breakout rooms', 'danger']);
    } finally {
      setIsClosing(false);
    }
  };

  const handleBroadcast = async () => {
    if (!roomId || !broadcastMessage.trim() || !hasSession) return;
    setIsBroadcasting(true);
    try {
      const response = await broadcastToBreakoutRooms({
        livestreamSessionId: roomId,
        message: broadcastMessage.trim(),
      });
      if (markAuthFailure(response) || response.error) {
        notify([getResponseError(response) || 'Failed to broadcast message', 'danger']);
        return;
      }
      setBroadcastMessage('');
      notify(['Message broadcast to all rooms', 'success']);
    } catch {
      notify(['Failed to broadcast message', 'danger']);
    } finally {
      setIsBroadcasting(false);
    }
  };

  if (!extendedWidgetState.showBreakoutRooms) return null;

  if (session.loading) {
    return null;
  }

  if (!hasSession || authFailed) {
    return (
      <div className="flex flex-col gap-2 z-50 p-4 overflow-y-auto" style={{ width: 'clamp(240px, 40ch, 50ch)' }}>
        <div className="text-sm font-semibold text-white">Breakout Rooms</div>
        <div className="rounded bg-[#1e1e1e] px-3 py-2 text-xs text-gray-300">
          Your session has expired. Sign in again to manage breakout rooms.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 z-50 p-4 overflow-y-auto" style={{ width: 'clamp(240px, 40ch, 50ch)' }}>
      <div className="flex items-center h-[60px] shrink-0">
        <span className="font-semibold text-white">Breakout Rooms</span>
      </div>

      {!hasOpenRooms ? (
        /* ── Create rooms ── */
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400">Number of rooms</span>
            <div className="flex items-center gap-2">
              <Button
                className="w-7 h-7 p-0 bg-[#2b2b2b] hover:bg-[#373737]"
                onClick={() => setRoomCount((c) => Math.max(1, c - 1))}
              >
                <Minus className="w-3 h-3" />
              </Button>
              <span className="w-6 text-center font-semibold text-white">{roomCount}</span>
              <Button
                className="w-7 h-7 p-0 bg-[#2b2b2b] hover:bg-[#373737]"
                onClick={() => setRoomCount((c) => Math.min(10, c + 1))}
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </div>
          <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={handleCreate} disabled={isCreating}>
            {isCreating ? 'Opening...' : 'Open Breakout Rooms'}
          </Button>
        </div>
      ) : (
        /* ── Active rooms ── */
        <div className="flex flex-col gap-3">
          {/* Assign participants */}
          {participantRows.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                Participants ({participantRows.length})
              </span>
              {participantRows.map((participant) => (
                <div
                  key={participant.identity}
                  className="flex items-center justify-between bg-[#1e1e1e] rounded px-2 py-1 gap-2"
                >
                  <span className="text-sm text-gray-200 truncate flex-1">{participant.displayName}</span>
                  <RoomAssignmentControl
                    identity={participant.identity}
                    assignedRoomId={participant.requestedRoomId}
                    currentRoomId={participant.currentRoomId}
                    openRooms={openRooms}
                    onAssign={handleAssign}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="text-[11px] text-gray-500">Blue fill = invited room. Green ring = room currently joined.</div>

          {/* Broadcast */}
          <div className="flex gap-2 mt-2">
            <input
              type="text"
              placeholder="Broadcast message to all rooms..."
              value={broadcastMessage}
              onChange={(e) => setBroadcastMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleBroadcast()}
              className="flex-1 text-sm bg-[#1e1e1e] text-white rounded px-2 py-1 outline-none border border-[#373737] focus:border-blue-500"
            />
            <Button
              className="bg-[#2b2b2b] hover:bg-[#373737] px-2 shrink-0"
              onClick={handleBroadcast}
              disabled={isBroadcasting || !broadcastMessage.trim()}
              title="Broadcast to all rooms"
            >
              <Megaphone className="w-4 h-4" />
            </Button>
          </div>

          {/* Close all */}
          <Button className="w-full bg-red-900 hover:bg-red-800" onClick={handleClose} disabled={isClosing}>
            <DoorClosed className="w-4 h-4 mr-2" />
            {isClosing ? 'Closing...' : 'Close All Rooms'}
          </Button>
        </div>
      )}
    </div>
  );
};
